import careTeamMascot from "@/assets/healic-mascot-care-team.png.asset.json";
import { Link } from "@tanstack/react-router";
import { useId, useState, cloneElement, isValidElement, type ReactElement } from "react";
import { toast } from "sonner";
import { notifyForm } from "@/lib/notifyForm";
import { PageShell } from "@/components/PageShell";
import { locations, specialties, contact } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Stethoscope,
  ArrowRight,
  Calendar,
  Baby,
  Bone,
  Ear,
  Eye,
  Slice,
  Droplets,
  Flower2,
  type LucideIcon,
} from "lucide-react";

const specialtyIcons: Record<string, LucideIcon> = {
  "general-surgery": Slice,
  gynaecology: Baby,
  "cosmetic-gynaecology": Flower2,
  orthopaedics: Bone,
  urology: Droplets,
  ent: Ear,
  ophthalmology: Eye,
};
import hospitalGurugram from "@/assets/hospital-gurugram.webp";
import hospitalGhaziabad from "@/assets/hospital-ghaziabad.webp";
import hospitalHaridwar from "@/assets/hospital-haridwar.webp";
import hospitalAgra from "@/assets/hospital-agra.webp";
import hospitalExterior from "@/assets/hospital-exterior.jpg";

export const locationImages: Record<string, string> = {
  "sohna-road-gurugram": hospitalGurugram,
  "indirapuram-ghaziabad": hospitalGhaziabad,
  haridwar: hospitalHaridwar,
  agra: hospitalAgra,
  muzaffarnagar: hospitalExterior,
};

export interface LocationPageConfig {
  slug: string;
  tagline: string;
  hours: string;
  highlights?: string[]; // kept for back-compat; unused
  faqs: { q: string; a: string }[];
  excludeSpecialties?: string[];
}

export function locationJsonLd(config: LocationPageConfig) {
  const loc = locations.find((l) => l.slug === config.slug);
  if (!loc) return [];
  return [
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Hospital",
        name: `Healic Hospital — ${loc.city}`,
        address: { "@type": "PostalAddress", streetAddress: loc.address },
        telephone: "+91-95887-95887",
        openingHours: "Mo-Su 08:00-22:00",
        url: `https://healic.in/locations/${loc.slug}`,
      }),
    },
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: config.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ];
}


export function LocationPage({ config }: { config: LocationPageConfig }) {
  const loc = locations.find((l) => l.slug === config.slug)!;
  const image = locationImages[loc.slug];
  const mapQuery = encodeURIComponent(`Healic Hospital ${loc.city} ${loc.address}`);
  const mapSrc = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const others = locations.filter((l) => l.slug !== loc.slug);
  const allDoctors = useMergedDoctors();
  const localDoctors = allDoctors.filter((d) => d.locationSlug === loc.slug);

  return (
    <PageShell>
      {/* Hero — light, matches reference */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-10 lg:px-8 lg:pt-14">
          <Link
            to="/locations"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent hover:opacity-80"
          >
            ← All Locations
          </Link>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            Healic Hospital — {loc.city}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{loc.address}</p>

          <div className="mt-8 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <div className="overflow-hidden rounded-3xl border border-border bg-cream shadow-soft">
              <img
                src={image}
                alt={`Healic Hospital ${loc.city}`}
                fetchPriority="high"
                decoding="async"
                width={1280}
                height={720}
                className="h-full max-h-[420px] w-full object-cover"
              />
            </div>

            <div className="flex flex-col gap-4">
              <a
                href={`tel:${contact.phoneTel}`}
                className="inline-flex items-center justify-center gap-2 self-start rounded-pill bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-cta hover:opacity-90"
              >
                <Phone className="h-4 w-4" /> {contact.phone}
              </a>
              <div className="rounded-2xl border border-border bg-cream p-5">
                <p className="text-base font-semibold text-foreground">Open Hours</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{config.hours}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Find us on the map */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <h2 className="font-display text-3xl font-bold">Find us on the map</h2>
        <div className="mt-6 overflow-hidden rounded-3xl border border-border shadow-soft">
          <iframe
            title={`Map of Healic Hospital ${loc.city}`}
            src={mapSrc}
            width="100%"
            height="380"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="block w-full"
          />
        </div>
      </section>

      {/* Our Treatment */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <h2 className="font-display text-3xl font-bold">Our Treatment</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {specialties.filter((s) => !config.excludeSpecialties?.includes(s.slug)).map((s) => {
            const Icon = specialtyIcons[s.slug] ?? Stethoscope;
            return (
              <Link
                key={s.slug}
                to="/specialties/$slug"
                params={{ slug: s.slug }}
                className="group rounded-2xl border border-border bg-background p-5 transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-accent border border-border shadow-sm">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-base font-semibold">{s.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.short}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent opacity-0 transition group-hover:opacity-100">
                  Know more <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Doctors (kept — useful per-city info) */}
      {localDoctors.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
          <h2 className="font-display text-3xl font-bold">Doctors at {loc.city}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {localDoctors.map((d) => {
              const profileSlug = doctorProfileSlug(d.slug, d.name);
              return (
                <Link
                  key={d.slug}
                  to="/doctors/$slug"
                  params={{ slug: profileSlug }}
                  className="group overflow-hidden rounded-2xl border border-border bg-background transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
                >
                  <DoctorPortrait
                    doctor={d}
                    className="aspect-[4/3] overflow-hidden"
                    imgClassName="transition group-hover:scale-105"
                  />
                  <div className="p-5">
                    <h3 className="font-display text-base font-semibold">{d.name}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{d.specialty}</p>
                    {d.experience && (
                      <p className="mt-2 inline-block rounded-pill bg-white border border-border px-3 py-1 text-xs font-semibold text-accent shadow-sm">
                        {d.experience}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}


      {/* Find Us in Your City */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <h2 className="font-display text-3xl font-bold">Find Us in Your City</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((l) => (
            <Link
              key={l.slug}
              to={locationHref(l.slug)}
              className="group overflow-hidden rounded-2xl border border-border bg-background shadow-soft transition hover:-translate-y-1 hover:shadow-card"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={locationImages[l.slug]}
                  alt={`Healic Hospital ${l.city}`}
                  loading="lazy"
                  width={768}
                  height={480}
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-display text-base font-semibold">{l.city}</h3>
                <p className="mt-2 text-xs text-muted-foreground">{l.address}</p>
                <p className="mt-3 text-xs font-semibold text-accent">{contact.phone}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQs + side card */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Answers</p>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Frequently asked{" "}
              <span
                className="italic text-accent"
                style={{ fontFamily: '"Instrument Serif", "DM Serif Display", Georgia, serif' }}
              >
                questions
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Clear answers to the questions families ask us most often — before booking a consult.
          </p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <div className="space-y-3">
            {config.faqs.map((f, idx) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-border bg-background p-5 shadow-soft transition hover:border-accent/50 open:border-accent open:shadow-card"
              >
                <summary className="flex cursor-pointer list-none items-center gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft font-display text-sm font-bold text-accent">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-sm font-semibold text-foreground sm:text-base">
                    {f.q}
                  </span>
                  <span
                    aria-hidden
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-cream text-foreground transition group-open:rotate-180 group-open:border-accent group-open:bg-accent group-open:text-accent-foreground"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:pl-[3.25rem]">
                  {f.a}
                </p>
              </details>
            ))}
          </div>

          <aside className="relative self-start overflow-hidden rounded-3xl bg-primary p-7 text-primary-foreground shadow-card">
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/30 blur-3xl"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-accent/20 blur-3xl"
              aria-hidden
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent ring-1 ring-white/20">
                <MessageCircle className="h-3 w-3" /> Need a hand?
              </span>
              <h3 className="mt-4 font-display text-2xl font-bold leading-tight">
                Still Unsure About Treatment?
              </h3>
              <p className="mt-3 text-sm leading-relaxed opacity-85">
                Our expert team will guide you through diagnosis, treatment options and recovery — clearly and patiently.
              </p>
              <div className="mt-6 flex flex-col gap-2.5">
                <a
                  href={`tel:${contact.phoneTel}`}
                  className="inline-flex items-center justify-center gap-2 rounded-pill bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-cta transition hover:opacity-90"
                >
                  <Phone className="h-4 w-4" /> Get Expert Advice
                </a>
                <a
                  href={`https://wa.me/${contact.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-pill bg-white/10 px-5 py-3 text-sm font-semibold ring-1 ring-white/20 transition hover:bg-white/15"
                >
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </div>
              <div className="mt-6 flex items-center gap-3 border-t border-white/15 pt-5">
                <img
                  src={careTeamMascot.url}
                  alt="Healic Care Team mascot"
                  width={48}
                  height={48}
                  loading="lazy"
                  className="h-12 w-12 rounded-full bg-white object-cover ring-2 ring-white/40"
                />
                <div className="text-xs opacity-85">
                  <p className="font-semibold">Healic Care Team</p>
                  <p>Replies within minutes · 7 Am to 12 Am</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Book an Appointment + Need help */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <LocationBookingForm locationName={`Healic — ${loc.city}`} />


          <aside className="flex flex-col gap-4">
            <div>
              <p className="font-display text-base font-semibold">Need help?</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Speak to our care team or book via WhatsApp.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-background p-5 shadow-soft">
              <p className="text-sm font-semibold">7 Am to 12 Am Care Helpline</p>
              <p className="mt-1 text-sm font-semibold">{contact.phone}</p>
              <a href={`mailto:${contact.email}`} className="mt-1 block text-xs text-accent">
                {contact.email}
              </a>
              <a
                href={`tel:${contact.phoneTel}`}
                className="mt-4 inline-flex w-full items-center justify-center rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Call Now
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-background p-5 shadow-soft">
              <p className="text-sm font-semibold">Book on WhatsApp</p>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                <li>• Instant chat</li>
                <li>• Share reports</li>
                <li>• Quick confirmation</li>
              </ul>
              <a
                href={`https://wa.me/${contact.whatsapp}`}
                className="mt-4 inline-flex w-full items-center justify-center rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Chat Now
              </a>
            </div>
            <div>
              <p className="text-xs font-semibold">Trusted by patients for:</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {["Affordable", "Trustworthy", "Patient focus", "Top Quality"].map((b) => (
                  <span
                    key={b}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-center text-xs font-semibold"
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Contact & Support */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <h2 className="font-display text-2xl font-bold">Contact & Support</h2>
          <p className="mt-1 text-sm text-muted-foreground">Quick help options get answers fast.</p>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            <ContactCard
              icon={<Phone className="h-5 w-5" />}
              title="7 Am to 12 Am Helpline"
              meta={contact.phone}
              note="Speak to our care team anytime"
              cta="Call Now"
              href={`tel:${contact.phoneTel}`}
            />
            <ContactCard
              icon={<Mail className="h-5 w-5" />}
              title="Email Support"
              meta={contact.email}
              note="Share reports or documents easily"
              cta="Email Now"
              href={`mailto:${contact.email}`}
              variant="secondary"
            />
            <ContactCard
              icon={<MessageCircle className="h-5 w-5" />}
              title="WhatsApp Chat"
              meta="Instant help. Quick replies."
              note="Share images & get confirmations fast"
              cta="Chat on WhatsApp"
              href={`https://wa.me/${contact.whatsapp}`}
              variant="whatsapp"
            />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId();
  const child = isValidElement(children) ? cloneElement(children, { id }) : children;
  return (
    <div className="block">
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold text-accent">{label}</label>
      {child}
    </div>
  );
}

function ContactCard({
  icon,
  title,
  meta,
  note,
  cta,
  href,
  variant = "primary",
}: {
  icon: React.ReactNode;
  title: string;
  meta: string;
  note: string;
  cta: string;
  href: string;
  variant?: "primary" | "secondary" | "whatsapp";
}) {
  const btn =
    variant === "whatsapp"
      ? "bg-[#25D366] text-white"
      : variant === "secondary"
        ? "bg-accent text-accent-foreground"
        : "bg-primary text-primary-foreground";
  return (
    <div className="rounded-2xl border border-border bg-background p-6 text-center shadow-soft">
      <h3 className="font-display text-base font-semibold">{title}</h3>
      <div className="mt-4 flex items-center justify-center gap-2 text-sm">
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-accent shadow-sm">
          {icon}
        </span>
        <div className="text-left">
          <p className="font-semibold">{meta}</p>
          <p className="text-xs text-muted-foreground">{note}</p>
        </div>
      </div>
      <a
        href={href}
        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-pill px-5 py-2.5 text-sm font-semibold ${btn}`}
      >
        {cta}
      </a>
    </div>
  );
}

function LocationBookingForm({ locationName }: { locationName: string }) {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    if (name.length < 2) return toast.error("Please enter your name");
    if (!/^[0-9+\-\s()]{7,20}$/.test(phone)) return toast.error("Please enter a valid phone");
    setSubmitting(true);
    const ok = await notifyForm({
      formType: "Location Appointment Request",
      source: locationName,
      fields: [
        { label: "Location", value: locationName },
        { label: "Doctor / Service", value: String(fd.get("service") ?? "") },
        { label: "Name", value: name },
        { label: "Phone", value: phone },
        { label: "Email", value: String(fd.get("email") ?? "") },
        { label: "Preferred Date", value: String(fd.get("date") ?? "") },
        { label: "Preferred Time", value: String(fd.get("time") ?? "") },
      ],
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Appointment requested! Our team will confirm shortly.");
      form.reset();
    } else {
      toast.error("Could not submit. Please call +91 95887 95887.");
    }
  };
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-background p-6 shadow-soft">
      <h2 className="font-display text-2xl font-bold">Book an Appointment</h2>
      <p className="mt-1 text-sm text-muted-foreground">Quick, secure booking — no redirects.</p>
      <div className="mt-6 grid gap-4">
        <Field label="Select Doctor / Service Type">
          <input name="service" type="text" placeholder="e.g. Dr. Priya Sharma — Gynae" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Preferred Date">
            <input name="date" type="date" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
          <Field label="Preferred Time">
            <input name="time" type="time" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full Name">
            <input name="name" type="text" placeholder="Your full name" required className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
          <Field label="Mobile Number">
            <input name="phone" type="tel" placeholder="+91" required className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
          </Field>
        </div>
        <Field label="Email (Optional)">
          <input name="email" type="email" placeholder="you@company.com" className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent" />
        </Field>
        <label className="flex items-start gap-2 text-xs text-muted-foreground">
          <input type="checkbox" defaultChecked aria-label="Consent to be contacted on phone and WhatsApp" className="mt-0.5 h-3.5 w-3.5 accent-primary" />
          I agree to be contacted on phone & WhatsApp for my appointment.
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center gap-2 self-start rounded-xl bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-cta hover:opacity-90 disabled:opacity-60"
        >
          <Calendar className="h-4 w-4" /> {submitting ? "Submitting..." : "Request Call Back"}
        </button>
      </div>
    </form>
  );
}
